import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { MatTabsModule } from '@angular/material/tabs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { UserPreferenceStore } from '../../../core/services/user-preference/user-preference.store';
import { DETAILS_SERVICE_TOKEN } from '../../../features/admin/modules/modules-entity/templates/details/details/details.service';

@Component({
  selector: 'formly-field-tabs',
  template: `
    <mat-tab-group
      [selectedIndex]="index"
      [preserveContent]="!!props.preserveContent"
      animationDuration="0ms"
      (selectedIndexChange)="saveTabSettings($event)">
      @for (tab of field.fieldGroup; track trackByFn($index, tab); let i = $index) {
        @if (tab.className !== 'hidden' && !tab.hide) {
          <mat-tab>
            <ng-template mat-tab-label>
              <span [attr.data-cy]="'tab-label-' + tab.key">{{ tab.props.label }}</span>
              @if (getTabErrorCount(tab.key) > 0) {
                <span
                  class="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5
                         text-xs font-semibold rounded-full leading-5 bg-red-500 text-white"
                  [attr.aria-label]="getTabErrorCount(tab.key) + ' fouten'">
                  {{ getTabErrorCount(tab.key) }}
                </span>
              }
              @if (getPendingDocsCount(tab) > 0) {
                <span
                  class="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5
                         text-xs font-semibold leading-5 rounded-full bg-amber-400 text-amber-900"
                  [attr.aria-label]="getPendingDocsCount(tab) + ' documenten in behandeling'">
                  {{ getPendingDocsCount(tab) }}
                </span>
              }
            </ng-template>
            <!-- Lazy rendering with matTabContent ensures proper change detection when switching tabs -->
            <!-- Fields in non-active tabs are created fresh when selected, reading current touched/invalid state -->
            <!-- Exception: props.preserveContent keeps visited tabs alive (opt-in, e.g. form builder settings) -->
            <ng-template matTabContent>
              <formly-field [field]="tab" [formlyAttributes]="field"></formly-field>
            </ng-template>
          </mat-tab>
        }
      }
    </mat-tab-group>

    <!-- Mobile scrollable buttons. Gated by a real breakpoint instead of CSS-only
         (sm:hidden): a display:none copy still instantiates every tab's formly
         subtree, doubling the whole form on desktop. -->
    @if (isHandset()) {
    <div class="block sm:hidden">
      <div class="overflow-x-auto scrollbar-hide">
        <div class="flex gap-2 pb-2 min-w-min px-2">
          @for (tab of field.fieldGroup; track trackByFn($index, tab); let i = $index) {
            <button
              [class.bg-primary]="index === i"
              [class.text-white]="index === i"
              [disabled]="tab.className === 'hidden'"
              (click)="saveTabSettings(i)"
              class="relative px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium
                     transition-colors duration-200
                     focus:outline-none focus:ring-2 focus:ring-primary
                     disabled:opacity-50 disabled:cursor-not-allowed">
              {{ tab.props?.label }}
              @if (getTabErrorCount(tab.key) > 0) {
                <span
                  class="absolute -top-1 -right-1 min-w-4 h-4 flex items-center justify-center
                         text-[10px] font-semibold rounded-full bg-red-500 text-white"
                  [attr.aria-label]="getTabErrorCount(tab.key) + ' fouten'">
                  {{ getTabErrorCount(tab.key) }}
                </span>
              }
              @if (getPendingDocsCount(tab) > 0) {
                <span
                  class="absolute -top-1 -right-1 min-w-4 h-4 flex items-center justify-center
                         text-[10px] font-semibold rounded-full bg-amber-400 text-amber-900"
                  [attr.aria-label]="getPendingDocsCount(tab) + ' documenten in behandeling'">
                  {{ getPendingDocsCount(tab) }}
                </span>
              }
            </button>
          }
        </div>
      </div>

      <div class="mt-4">
        <!-- Only the active tab lives in the DOM (matches the desktop matTabContent
             semantics); [class.hidden] kept all tabs' subtrees instantiated. This
             also drops the old className==='hidden' exception, so hidden tabs now
             render on neither viewport (matching the desktop @if above). -->
        @for (tab of field.fieldGroup; track trackByFn($index, tab); let i = $index) {
          @if (index === i) {
            <div>
              <formly-field [field]="tab"></formly-field>
            </div>
          }
        }
      </div>
    </div>
    }
  `,
  styles: `
    ::ng-deep formly-field .mat-mdc-tab-group .mat-mdc-tab-header .mat-mdc-tab-label-container {
      margin: 0 !important;
    }
  `,
  imports: [FormlyModule, MatTabsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldTabs extends FieldType implements OnInit {
  index = 0;
  userPreferanceStore = inject(UserPreferenceStore);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  readonly #detailsService = inject(DETAILS_SERVICE_TOKEN, { optional: true });
  // Tailwind `sm` = 640px, matching the sm:hidden class on the mobile block.
  readonly isHandset = toSignal(
    inject(BreakpointObserver)
      .observe('(max-width: 639.98px)')
      .pipe(map(result => result.matches)),
    { initialValue: false }
  );

  trackByFn(_index: number, item: any): any {
    return item.id;
  }

  ngOnInit() {
    this.index = this.getTabIndex();

    // Subscribe to tab switch requests from detailsService
    if (this.#detailsService?.switchTab$) {
      this.#detailsService.switchTab$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tabKey: string) => {
        if (this.switchToTab(tabKey)) {
          this.cdr.markForCheck();
        }
      });
    }

    // Subscribe to tab error notifications from detailsService
    if (this.#detailsService?.tabErrorsChanged$) {
      this.#detailsService.tabErrorsChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.cdr.markForCheck();
      });
    }
  }

  getTabIndex() {
    if (!this.#shouldPersistTabIndex()) {
      return 0;
    }
    return this.userPreferanceStore.userPreference()?.ui[this.field.id] || 0;
  }

  saveTabSettings(index: number) {
    this.index = index;
    this.cdr.markForCheck();

    if (!this.#shouldPersistTabIndex()) {
      return;
    }
    // Update local state immediately for responsive UI
    this.userPreferanceStore.editUIImmediate({
      [this.field.id]: index,
    });
    // Debounced API call to update the server
    this.userPreferanceStore.editUIDebounced({
      [this.field.id]: index,
    });
  }

  /**
   * Persistence is opt-out via props.persistSelectedTab === false — set on the
   * admin form-builder settings tabs, whose runtime field.id is regenerated every
   * build so a stored index is never read back. NOT inferred from the id shape:
   * saved module definitions legitimately carry stable ids like
   * 'formly_4291_input_id_1', so a 'formly_' prefix check would break real entry
   * pages. Also skipped for the admin form preview and unsaved entries.
   */
  #shouldPersistTabIndex(): boolean {
    if (this.props.persistSelectedTab === false) return false;
    if (this.options.formState?.adminPreview) return false;
    return !!this.options.formState?.entry?.id;
  }

  /**
   * Get the error count for a specific tab from detailsService signal
   */
  getTabErrorCount(tabKey: string | number | undefined): number {
    if (!tabKey) return 0;
    if (!this.#detailsService?.tabErrorCounts) return 0;
    const tabErrors = this.#detailsService.tabErrorCounts() as Map<string, number>;
    return tabErrors?.get(String(tabKey)) || 0;
  }

  /**
   * Returns the pending-documents count when this tab contains a linked-items
   * field connected to the documenten module. Returns 0 otherwise.
   *
   * The count comes from the DetailsService signal updated by FormlyLinkedItemsComponent
   * after it fetches the linked documenten entries (Task 7.3 / Task 7.2).
   *
   * // TODO Task 7.4: connect completion-action visibility to this count once
   * // the "Markeer documenten compleet" interface action (Phase 6) is implemented.
   * // The action should only be visible when pendingDocsCount() === 0.
   */
  getPendingDocsCount(tab: { fieldGroup?: Array<{ type?: string }> }): number {
    if (!this.#detailsService?.pendingDocsCount) return 0;
    const count = this.#detailsService.pendingDocsCount();
    if (count === 0) return 0;

    // Only show on a tab that has at least one linked-items field
    const hasLinkedItems = tab.fieldGroup?.some(f => f.type === 'linked-items') ?? false;
    return hasLinkedItems ? count : 0;
  }

  /**
   * Switch to a specific tab by its key
   * Returns true if the tab was found and switched to
   */
  public switchToTab(tabKey: string): boolean {
    if (!this.field.fieldGroup) return false;

    const tabIndex = this.field.fieldGroup.findIndex(
      tab => String(tab.key) === tabKey && tab.className !== 'hidden' && !tab.hide
    );

    if (tabIndex >= 0) {
      this.saveTabSettings(tabIndex);
      return true;
    }

    return false;
  }
}
